import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as networkSelectors from 'data/network/network.selectors';
import * as afWhitelistSelectors from 'data/afWhitelist/afWhitelist.selectors';
import { RootState } from 'data/root.reducers';
import * as afWhitelistActions from 'data/afWhitelist/afWhitelist.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as distributorActions from 'data/distributor/distributor.actions';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { Distributor, DistributorData } from 'data/distributor/distributor.types';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { AdaptedAfWhitelist } from 'data/afWhitelist/afWhitelist.types';
import AfWhitelistForm from 'routes/AfWhitelist/components/AfWhitelistForm';
import * as types from 'data/afWhitelist/afWhitelist.types';
import { FormTemplate } from 'data/template/template.types';
import { afWhitelistTemplate } from '../../templates';
import {
 adaptTemplateVisibilityMode,
 adaptTemplateOptions,
 adaptAfWhitelistData
} from '../../utils/afWhitelistTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.AdaptedAfWhitelist
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedAfWhitelist: afWhitelistSelectors
    .getSelectedAfWhitelist(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedAfWhitelist: afWhitelistSelectors
    .getSelectedAfWhitelist(state)[0],
  distributors: distributorSelectors.getDistributors(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunction: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  afWhitelist: afWhitelistSelectors.getAfWhitelist(state),
});

const mapDispatch = {
  getDistributors: distributorActions.getDistributors,
  goToAfWhitelistList: afWhitelistActions.goToAfWhitelistList,
  goToAfWhitelistEdit: afWhitelistActions.goToAfWhitelistEdit,
  getAfWhitelist: afWhitelistActions.getAfWhitelist,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const AfWhitelistView = ({
  history,
  selectedAfWhitelist,
  deliveryFunctionId,
  distributors,
  enabledAccessFunction,
  availableAccessFunction,
  afWhitelist,
  goToAfWhitelistList,
  goToAfWhitelistEdit,
  getAfWhitelist,
  getDistributors
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<AdaptedAfWhitelist>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(afWhitelistTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedAfWhitelist?.id;
  useEffect(() => {
    if (id) {
      getAfWhitelist(String(id));
      getDistributors(deliveryFunctionId);
    }
  }, [id, getAfWhitelist, deliveryFunctionId, getDistributors]);

  useEffect(() => {
    if (afWhitelist != null && template != null && distributors != null && (!id || (id && afWhitelist.id === id))) {
      const clonedAfWhitelist = cloneDeep(afWhitelist);
      let clonedTemplate: FormTemplate | undefined = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      const adaptedAfWhitelist = adaptAfWhitelistData(clonedAfWhitelist, distributors);

      clonedTemplate = adaptTemplateOptions(clonedTemplate, distributors, enabledAccessFunction, availableAccessFunction, adaptedAfWhitelist);
      setTemplate(clonedTemplate);
      setFormData(adaptedAfWhitelist);
    }
  }, [afWhitelist, id, distributors, enabledAccessFunction]);

  const handleClickEdit = () => goToAfWhitelistEdit(history);

  const handleCancel = () => goToAfWhitelistList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AdaptedAfWhitelist);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <AfWhitelistForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (afWhitelist == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="AfWhitelistView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('afWhitelist.view.title'))}
          <div>
            {renderForm()}
          </div>
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(AfWhitelistView));
